/** Public: Forgot password (always the same reply) and Reset / Set password from a one-time 1-hour link. */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { btnPrimary, errorText, inputCls } from '../components/finance/financeUi';

const Shell = ({ title, children, testid }) => (
  <div className="grid min-h-screen place-items-center bg-muted/30 p-4" data-testid={testid}>
    <div className="w-full max-w-md space-y-5 rounded-2xl border bg-card p-8 shadow-sm">
      <h1 className="text-3xl font-semibold tracking-tight">{title}</h1>
      {children}
      <Link to="/login" className="block text-sm text-primary hover:underline" data-testid="back-to-login">Back to sign in</Link>
    </div>
  </div>
);

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { setSent((await api.request('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) })).message); } catch (err) { toast.error(errorText(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Forgot password" testid="forgot-password-page">
      {sent ? <p className="rounded-lg bg-emerald-500/10 p-3 text-sm text-emerald-800" data-testid="forgot-sent">{sent}</p> : (
        <form onSubmit={submit} className="space-y-3">
          <p className="text-sm text-muted-foreground">Enter your email and we'll send a link to reset your password.</p>
          <input type="email" required className={inputCls} placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" />
          <button type="submit" className={`${btnPrimary} w-full`} disabled={busy} data-testid="forgot-submit">{busy ? 'Sending…' : 'Send reset link'}</button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const invite = params.get('invite') === '1';
  const navigate = useNavigate();
  const [state, setState] = useState('checking');
  const [pw, setPw] = useState({ a: '', b: '' });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.request(`/auth/reset-password/${encodeURIComponent(token)}`).then(() => setState('ok')).catch(() => setState('bad')); }, [token]);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.a !== pw.b) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try { await api.request('/auth/reset-password', { method: 'POST', body: JSON.stringify({ token, password: pw.a }) }); toast.success('Password saved — sign in with your new password'); navigate('/login'); } catch (err) { toast.error(errorText(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title={invite ? 'Set your password' : 'Reset password'} testid="reset-password-page">
      {state === 'checking' && <p className="text-sm text-muted-foreground">Checking your link…</p>}
      {state === 'bad' && <p className="rounded-lg bg-rose-500/10 p-3 text-sm text-rose-800" data-testid="reset-invalid">This link has expired or was already used. <Link to="/forgot-password" className="font-medium underline">Request a new one</Link>.</p>}
      {state === 'ok' && (
        <form onSubmit={submit} className="space-y-3">
          <p className="text-xs text-muted-foreground">At least 8 characters with upper and lower case letters and a number.</p>
          <input type="password" required className={inputCls} placeholder="New password" value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} data-testid="reset-password" />
          <input type="password" required className={inputCls} placeholder="Confirm new password" value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} data-testid="reset-password-confirm" />
          <button type="submit" className={`${btnPrimary} w-full`} disabled={busy} data-testid="reset-submit">{busy ? 'Saving…' : 'Save password'}</button>
        </form>
      )}
    </Shell>
  );
}
